import { apiClient } from './client';
import { 
  Position, 
  PortfolioResponse, 
  PortfolioHistoryResponse, 
  ClosePositionRequest, 
  ClosePositionResponse, 
  ModifyPositionRequest,
  PortfolioFilters,
  PortfolioHistoryFilters,
  Transaction,
  TransactionFilters,
  TransactionResponse,
  PortfolioAnalytics,
  PerformanceChartData,
  PerformanceDataPoint
} from '../../types/portfolio';

export class PortfolioService {
  private apiClient = apiClient;

  /**
   * Get open positions for an account
   */
  async getOpenPositions(filters: PortfolioFilters): Promise<PortfolioResponse> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('accountId', filters.accountId);
      
      if (filters.page) queryParams.append('page', filters.page.toString());
      if (filters.limit) queryParams.append('limit', filters.limit.toString());

      const data = await this.apiClient.get<PortfolioResponse>(`/v1/portfolio?${queryParams.toString()}`);
      return data;
    } catch (error) {
      console.error('Error fetching open positions:', error);
      throw error;
    }
  }

  /**
   * Get details for a specific position
   */
  async getPositionDetails(positionId: string): Promise<Position> {
    try {
      const data = await this.apiClient.get<Position>(`/v1/portfolio/${positionId}`);
      return data;
    } catch (error) {
      console.error('Error fetching position details:', error);
      throw error;
    }
  }

  /**
   * Close an open position (full or partial)
   */
  async closePosition(positionId: string, closeRequest?: ClosePositionRequest): Promise<ClosePositionResponse> {
    try {
      const data = await this.apiClient.post<ClosePositionResponse>(`/v1/portfolio/${positionId}/close`, closeRequest || {});
      return data;
    } catch (error) {
      console.error('Error closing position:', error);
      throw error;
    }
  }

  /**
   * Modify position parameters (e.g., take profit level)
   */
  async modifyPosition(positionId: string, modifyRequest: ModifyPositionRequest): Promise<Position> {
    try {
      const data = await this.apiClient.put<Position>(`/v1/portfolio/${positionId}/parameters`, modifyRequest);
      return data;
    } catch (error) {
      console.error('Error modifying position:', error);
      throw error;
    }
  }

  /**
   * Get closed positions history
   */
  async getPositionHistory(filters: PortfolioHistoryFilters): Promise<PortfolioHistoryResponse> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('accountId', filters.accountId);
      
      if (filters.from) queryParams.append('from', filters.from);
      if (filters.to) queryParams.append('to', filters.to);
      if (filters.product) queryParams.append('product', filters.product);
      if (filters.page) queryParams.append('page', filters.page.toString());
      if (filters.limit) queryParams.append('limit', filters.limit.toString());

      const data = await this.apiClient.get<PortfolioHistoryResponse>(`/v1/portfolio/history?${queryParams.toString()}`);
      return data;
    } catch (error) {
      console.error('Error fetching position history:', error);
      throw error;
    }
  }

  /**
   * Get account transaction history
   */
  async getTransactions(filters: TransactionFilters): Promise<TransactionResponse> {
    try {
      const queryParams = new URLSearchParams();
      
      if (filters.from) queryParams.append('from', filters.from);
      if (filters.to) queryParams.append('to', filters.to);
      if (filters.type) queryParams.append('type', filters.type);
      if (filters.page) queryParams.append('page', filters.page.toString());
      if (filters.limit) queryParams.append('limit', filters.limit.toString());

      const data = await this.apiClient.get<TransactionResponse>(`/v1/accounts/${filters.accountId}/transactions?${queryParams.toString()}`);
      return data;
    } catch (error) {
      console.error('Error fetching transactions:', error);
      throw error;
    }
  }

  /**
   * Get portfolio analytics and statistics
   */
  async getPortfolioAnalytics(accountId: string, timeframe: '7D' | '30D' | '90D' | '1Y' | 'All' = '30D'): Promise<PortfolioAnalytics> {
    try {
      // Get portfolio history and calculate analytics
      const historyFilters: PortfolioHistoryFilters = {
        accountId,
        limit: 1000, // Get more data for accurate analytics
        from: this.getTimeframeStartDate(timeframe)
      };

      const historyResponse = await this.getPositionHistory(historyFilters);
      const positions = historyResponse.positions;

      // Calculate analytics from position data
      const analytics: PortfolioAnalytics = this.calculateAnalytics(positions, accountId);
      
      return analytics;
    } catch (error) {
      console.error('Error calculating portfolio analytics:', error);
      throw error;
    }
  }

  /**
   * Get performance chart data
   */
  async getPerformanceChartData(accountId: string, timeframe: '7D' | '30D' | '90D' | '1Y' | 'All'): Promise<PerformanceChartData> {
    try {
      const historyFilters: PortfolioHistoryFilters = {
        accountId,
        limit: 1000,
        from: this.getTimeframeStartDate(timeframe)
      };

      const historyResponse = await this.getPositionHistory(historyFilters);
      const positions = historyResponse.positions;

      // Convert positions to performance data points
      const dataPoints = this.aggregatePerformanceData(positions, timeframe);

      return {
        dataPoints,
        timeframe
      };
    } catch (error) {
      console.error('Error fetching performance chart data:', error);
      throw error;
    }
  }

  /**
   * Export transaction data to CSV format
   */
  async exportTransactions(filters: TransactionFilters): Promise<string> {
    try {
      // Get all transactions for export
      const allTransactions: Transaction[] = [];
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        const response = await this.getTransactions({
          ...filters,
          page,
          limit: 200
        });

        allTransactions.push(...response.transactions);
        hasMore = page < response.pagination.totalPages;
        page++;
      }

      // Convert to CSV
      return this.convertTransactionsToCSV(allTransactions);
    } catch (error) {
      console.error('Error exporting transactions:', error);
      throw error;
    }
  }

  /**
   * Format position for UI display
   */
  formatPositionForUI(position: Position) {
    const formatTime = (timestamp: string) => {
      return new Date(timestamp).toLocaleString();
    };

    const formatCurrency = (amount: number, currency: string) => {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
      }).format(amount);
    };

    const formatPercentage = (percentage: number) => {
      return `${percentage >= 0 ? '+' : ''}${percentage.toFixed(2)}%`;
    };

    return {
      ...position,
      formattedStake: formatCurrency(position.stake, position.currency),
      formattedPayout: formatCurrency(position.payout, position.currency),
      formattedCurrentPnL: position.current_pnl 
        ? formatCurrency(position.current_pnl.amount, position.currency)
        : 'N/A',
      formattedCurrentPnLPercentage: position.current_pnl 
        ? formatPercentage(position.current_pnl.percentage)
        : 'N/A',
      formattedEntryTime: formatTime(position.entry_time),
      formattedExpiryTime: formatTime(position.expiry_time),
      formattedClosedTime: position.closed_time ? formatTime(position.closed_time) : null,
      isProfit: position.current_pnl ? position.current_pnl.amount >= 0 : false,
      timeToExpiry: this.getTimeToExpiry(position.expiry_time),
      productDisplayName: this.getProductDisplayName(position.product),
      categoryDisplayName: this.getCategoryDisplayName(position.category)
    };
  }

  /**
   * Check if position can be closed early
   */
  canClosePosition(position: Position): boolean {
    // Generally, digital options cannot be closed early
    // Accumulators and some vanilla options can be closed
    return position.product === 'accumulator' || position.status === 'open';
  }

  /**
   * Check if position parameters can be modified
   */
  canModifyPosition(position: Position): boolean {
    // Only certain products like accumulators support parameter modification
    return position.product === 'accumulator' && position.status === 'open';
  }

  /**
   * Get real-time P&L calculation
   */
  calculateRealtimePnL(position: Position, currentSpot: number): { amount: number; percentage: number } {
    if (!position.current_spot) {
      position.current_spot = currentSpot;
    }

    // This is a simplified calculation - actual P&L depends on product type
    let pnlAmount = 0;
    let pnlPercentage = 0;

    switch (position.product) {
      case 'rise_fall':
        // For rise/fall, P&L is binary (win full payout or lose stake)
        if (position.product_params.direction === 'rise') {
          pnlAmount = currentSpot > position.entry_spot ? position.payout - position.stake : -position.stake;
        } else {
          pnlAmount = currentSpot < position.entry_spot ? position.payout - position.stake : -position.stake;
        }
        break;
      
      case 'higher_lower':
        // Similar to rise/fall but with barrier
        const barrier = position.product_params.barrier_spot || position.entry_spot;
        if (position.product_params.direction === 'higher') {
          pnlAmount = currentSpot > barrier ? position.payout - position.stake : -position.stake;
        } else {
          pnlAmount = currentSpot < barrier ? position.payout - position.stake : -position.stake;
        }
        break;
      
      case 'accumulator':
        // For accumulators, P&L grows with successful ticks
        const tickCount = position.product_params.tick_count || 0;
        const growthRate = position.product_params.growth_rate || 1;
        pnlAmount = position.stake * (growthRate / 100) * tickCount;
        break;
      
      default:
        // Use current_pnl from API if available
        pnlAmount = position.current_pnl?.amount || 0;
    }

    pnlPercentage = position.stake > 0 ? (pnlAmount / position.stake) * 100 : 0;

    return { amount: pnlAmount, percentage: pnlPercentage };
  }

  /**
   * Calculate analytics from position data
   */
  private calculateAnalytics(positions: Position[], _accountId: string): PortfolioAnalytics {
    const wonPositions = positions.filter(p => p.status === 'won');
    const lostPositions = positions.filter(p => p.status === 'lost');
    const totalTrades = positions.length;

    // Calculate total stake (removed - not currently used)
    // const totalStake = positions.reduce((sum, p) => sum + p.stake, 0);
    const totalPnL = positions.reduce((sum, p) => sum + (p.pnl?.amount || 0), 0);
    const winRate = totalTrades > 0 ? wonPositions.length / totalTrades : 0;

    const averageWin = wonPositions.length > 0 
      ? wonPositions.reduce((sum, p) => sum + (p.pnl?.amount || 0), 0) / wonPositions.length 
      : 0;

    const averageLoss = lostPositions.length > 0 
      ? Math.abs(lostPositions.reduce((sum, p) => sum + (p.pnl?.amount || 0), 0)) / lostPositions.length 
      : 0;

    // Calculate daily P&L for best/worst day
    const dailyPnL = this.calculateDailyPnL(positions);
    const bestDay = Math.max(...Object.values(dailyPnL), 0);
    const worstDay = Math.min(...Object.values(dailyPnL), 0);

    const grossProfit = wonPositions.reduce((sum, p) => sum + Math.abs(p.pnl?.amount || 0), 0);
    const grossLoss = lostPositions.reduce((sum, p) => sum + Math.abs(p.pnl?.amount || 0), 0);
    const profitFactor = grossLoss > 0 ? grossProfit / grossLoss : 0;

    // Simplified Sharpe ratio calculation
    const returns = Object.values(dailyPnL);
    const avgReturn = returns.reduce((sum, r) => sum + r, 0) / returns.length;
    const returnStdDev = Math.sqrt(returns.reduce((sum, r) => sum + Math.pow(r - avgReturn, 2), 0) / returns.length);
    const sharpeRatio = returnStdDev > 0 ? avgReturn / returnStdDev : 0;

    // Time-based P&L calculations
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekStart = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

    const todayPnL = positions
      .filter(p => new Date(p.closed_time || p.entry_time) >= todayStart)
      .reduce((sum, p) => sum + (p.pnl?.amount || 0), 0);

    const weekPnL = positions
      .filter(p => new Date(p.closed_time || p.entry_time) >= weekStart)
      .reduce((sum, p) => sum + (p.pnl?.amount || 0), 0);

    const monthPnL = positions
      .filter(p => new Date(p.closed_time || p.entry_time) >= monthStart)
      .reduce((sum, p) => sum + (p.pnl?.amount || 0), 0);

    return {
      totalBalance: 10000, // This would come from account balance API
      totalPnL,
      winRate,
      totalTrades,
      averageWin,
      averageLoss,
      bestDay,
      worstDay,
      profitFactor,
      sharpeRatio,
      todayPnL,
      weekPnL,
      monthPnL
    };
  }

  /**
   * Calculate daily P&L aggregation
   */
  private calculateDailyPnL(positions: Position[]): Record<string, number> {
    const dailyPnL: Record<string, number> = {};

    positions.forEach(position => {
      const date = new Date(position.closed_time || position.entry_time).toISOString().split('T')[0];
      if (!dailyPnL[date]) {
        dailyPnL[date] = 0;
      }
      dailyPnL[date] += position.pnl?.amount || 0;
    });

    return dailyPnL;
  }

  /**
   * Aggregate performance data by timeframe
   */
  private aggregatePerformanceData(positions: Position[], _timeframe: string): PerformanceDataPoint[] {
    const dataPoints: PerformanceDataPoint[] = [];
    const dailyData: Record<string, { balance: number; pnl: number; trades: number }> = {};

    let runningBalance = 10000; // Starting balance

    // Group positions by date
    positions.forEach(position => {
      const date = new Date(position.closed_time || position.entry_time).toISOString().split('T')[0];
      if (!dailyData[date]) {
        dailyData[date] = { balance: 0, pnl: 0, trades: 0 };
      }
      dailyData[date].pnl += position.pnl?.amount || 0;
      dailyData[date].trades += 1;
    });

    // Convert to data points with running balance
    const sortedDates = Object.keys(dailyData).sort();
    sortedDates.forEach(date => {
      const dayData = dailyData[date];
      runningBalance += dayData.pnl;
      dataPoints.push({
        date,
        balance: runningBalance,
        pnl: dayData.pnl,
        trades: dayData.trades
      });
    });

    return dataPoints;
  }

  /**
   * Get start date for timeframe
   */
  private getTimeframeStartDate(timeframe: string): string {
    const now = new Date();
    let startDate: Date;

    switch (timeframe) {
      case '7D':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case '30D':
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case '90D':
        startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        break;
      case '1Y':
        startDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
        break;
      case 'All':
      default:
        startDate = new Date(2020, 0, 1); // Far back date for "All"
        break;
    }

    return startDate.toISOString();
  }

  /**
   * Convert transactions to CSV format
   */
  private convertTransactionsToCSV(transactions: Transaction[]): string {
    const headers = ['Date', 'Type', 'Description', 'Amount', 'Balance', 'Reference'];
    const csvRows = [headers.join(',')];

    transactions.forEach(transaction => {
      const row = [
        new Date(transaction.timestamp).toLocaleString(),
        `${transaction.type} - ${transaction.subType}`,
        `"${transaction.description}"`,
        transaction.amount.toString(),
        transaction.balance.toString(),
        transaction.reference
      ];
      csvRows.push(row.join(','));
    });

    return csvRows.join('\n');
  }

  /**
   * Get time remaining until expiry
   */
  private getTimeToExpiry(expiryTime: string): string {
    const now = new Date();
    const expiry = new Date(expiryTime);
    const diffMs = expiry.getTime() - now.getTime();

    if (diffMs <= 0) {
      return 'Expired';
    }

    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return `${diffDays}d ${diffHours % 24}h`;
    } else if (diffHours > 0) {
      return `${diffHours}h ${diffMinutes % 60}m`;
    } else {
      return `${diffMinutes}m`;
    }
  }

  /**
   * Get display name for product type
   */
  private getProductDisplayName(product: string): string {
    const productNames: Record<string, string> = {
      'rise_fall': 'Rise/Fall',
      'higher_lower': 'Higher/Lower',
      'matches_differs': 'Matches/Differs',
      'even_odd': 'Even/Odd',
      'over_under': 'Over/Under',
      'accumulator': 'Accumulator'
    };
    return productNames[product] || product;
  }

  /**
   * Get display name for category
   */
  private getCategoryDisplayName(category: string): string {
    const categoryNames: Record<string, string> = {
      'directional': 'Directional',
      'digit-based': 'Digit-Based',
      'growth-based': 'Growth-Based'
    };
    return categoryNames[category] || category;
  }
}

// Export singleton instance
export const portfolioService = new PortfolioService();
